import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import add from 'date-fns/add'
import format from 'date-fns/format'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import * as Yup from 'yup'

import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Label } from '@/@bothrs-ui/web/components/forms/label'
import { FlexColumn, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useAuthenticedSession } from '@/modules/auth/hooks/useAuthenticatedSession'
import { useBiometrics } from '@/modules/biometrics/hooks/useBiometrics'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { IDLE_TIMEOUT, SESSION_DURATION, WARNING_PERCENTAGE } from '@/modules/core/constants/idle-timer.constants'
import { LanguageSelector } from '@/modules/i18n/components/LanguageSelector'
import { BadgeTest } from '@/modules/shared/components/badge-test/BadgeTest'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useDebugSettings } from '../../hooks/useDebugSettings'

import type { FC } from 'react'

const minIdleTimeoutInSeconds = 10
const minSessionDurationInSeconds = 60

const DebugSettingsSchema = Yup.object().shape({
  idleTimeout: Yup.number()
    .required(i18n._(globalDefinitions.required))
    .min(minIdleTimeoutInSeconds, 'De automatische vergrendeling moet minimaal 10 seconden zijn'),
  sessionDuration: Yup.number()
    .required(i18n._(globalDefinitions.required))
    .min(minSessionDurationInSeconds, 'De sessie duur moet minimaal 1 minuut zijn')
    .when('idleTimeout', (idleTimeout, schema) => {
      return schema.test({
        test: (sessionDuration: number) => sessionDuration > idleTimeout,
        message: 'De sessie duur moet langer zijn dan de `automatisch vergrendelen`-waarde.',
      })
    }),
  warningPercentage: Yup.number().required(i18n._(globalDefinitions.required)).max(80),
})

export const DebugSettingsPage: FC = () => {
  // -- Hooks --
  const theme = useTheme()
  const navigate = useNavigate()

  const { showSuccessToast } = useToast()
  const { isAvailable } = useBiometrics()
  const { biometricPermission } = useBiometricsPermission()

  const { debugSettings, updateDebugSettings } = useDebugSettings()
  const { lastLoggedInAt, updateLastLoggedIn } = useAuthenticedSession()

  return (
    <ContentPageLayout
      noScroll
      header={
        <AppHeader
          primaryAction={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate(-1)
            },
          }}
        />
      }
    >
      <Padding pt={theme.UI.Spacing.Space4}>
        <H3 noMargin>
          {t({
            id: 'profile.debug-settings.page-title',
            message: 'Debug Settings',
          })}
        </H3>
      </Padding>

      <LanguageSelector />

      <FlexColumn gap={theme.UI.Spacing.Space2}>
        <Formik
          initialValues={{
            idleTimeout: (debugSettings?.idleTimeout || IDLE_TIMEOUT) / 1000,
            sessionDuration: (debugSettings?.sessionDuration || SESSION_DURATION) / 1000,
            warningPercentage: (debugSettings?.warningPercentage || WARNING_PERCENTAGE) * 100,
          }}
          validationSchema={DebugSettingsSchema}
          onSubmit={(values) => {
            updateLastLoggedIn()
            updateDebugSettings({
              idleTimeout: values.idleTimeout * 1000,
              sessionDuration: values.sessionDuration * 1000,
              warningPercentage: values.warningPercentage / 100,
            })

            showSuccessToast({
              message: t({
                id: 'profile.debug-settings.success',
                message: 'Settings saved',
              }),
            })
          }}
        >
          {({ isValid, values }) => (
            <Form id='debug-settings-form'>
              {/* Idle timeout */}
              <Margin mt={theme.UI.Spacing.Space4}>
                {/* IDLE Title */}
                <Label color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
                  {t({
                    id: 'profile.debug-settings.session-title',
                    message: 'Sessie Settings',
                  })}
                </Label>
                {/* IDLE timeout input  */}
                <div>
                  <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                    {t({
                      id: 'profile.debug-settings.idle-timeout',
                      message:
                        'App vergrendelen na (in seconden). Dit zal de app vergrendelen maar je niet uitloggen *',
                    })}
                  </Span>
                  <Input name='idleTimeout' type='number' inputMode='numeric' noMargin required />
                </div>

                {isAvailable && biometricPermission ? (
                  <div>
                    <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                      {t({
                        id: 'profile.debug-settings.auto-logout-after',
                        message:
                          'Geforceerd opnieuw inloggen met eHealth na (in seconden). Sluit de app of ga in de achtergrond om dit te triggeren *',
                      })}
                    </Span>
                    <Input name='sessionDuration' type='number' inputMode='numeric' noMargin required />
                  </div>
                ) : null}

                <div>
                  <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                    {t({
                      id: 'profile.debug-settings.warning-percentage',
                      message: 'Waarschuwing `app vergrendelen` tonen na x-percent (in percentage) *',
                    })}
                  </Span>
                  <Input name='warningPercentage' type='number' inputMode='numeric' noMargin required />
                </div>

                {/* Info about logout */}
                {lastLoggedInAt ? (
                  <div>
                    <P
                      textAlign='center'
                      type='BodyMediumRegular'
                      color={theme.Colors.Neutrals.Darkest}
                      style={{ fontWeight: 400 }}
                    >
                      {t({
                        id: 'profile.debug-settings.last-loggin',
                        message: 'Laatste login was op {loginTime}',
                        values: {
                          loginTime: format(new Date(lastLoggedInAt), 'dd/MM/yyyy HH:mm:ss'),
                        },
                      })}
                    </P>

                    <P
                      textAlign='center'
                      type='BodyMediumRegular'
                      color={theme.Colors.Neutrals.Darkest}
                      style={{ fontWeight: 400 }}
                    >
                      {t({
                        id: 'profile.debug-settings.auto-logout-info',
                        message:
                          'Gebruiker zal op <strong>{logoutTime}</strong> opnieuw met eHealth moeten inloggen. Gelieve app te sluiten of in de achtergrond te gaan om dit te triggeren.',
                        values: {
                          logoutTime: format(
                            add(new Date(lastLoggedInAt), {
                              seconds: values?.sessionDuration + values?.idleTimeout,
                            }),
                            'dd/MM/yyyy HH:mm:ss',
                          ),
                        },
                      })}
                    </P>
                  </div>
                ) : null}
              </Margin>
              {/* SUBMIT CTA */}
              <Margin mt={theme.UI.Spacing.Space8}>
                <Button variant='Primary' type='submit' disabled={!isValid}>
                  {t({
                    id: 'profile.debug-settings.save',
                    message: 'Opslaan',
                  })}
                </Button>
              </Margin>

              <Margin mt={theme.UI.Spacing.Space2}>
                <Button
                  variant='Secondary'
                  onClick={() =>
                    updateDebugSettings({
                      sessionDuration: SESSION_DURATION,
                      idleTimeout: IDLE_TIMEOUT,
                      warningPercentage: WARNING_PERCENTAGE,
                    })
                  }
                >
                  {t({
                    id: 'profile.debug-settings.reset',
                    message: 'Reset naar standaard waarden',
                  })}
                </Button>
              </Margin>
            </Form>
          )}
        </Formik>

        {/* App Badge Test */}
        <Margin mt={theme.UI.Spacing.Space8}>
          <Label color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
            {t({
              id: 'profile.debug-settings.app-badge',
              message: 'App Badge Settings',
            })}
          </Label>
          <Margin mt={theme.UI.Spacing.Space2}>
            <BadgeTest />
          </Margin>
        </Margin>
      </FlexColumn>
    </ContentPageLayout>
  )
}
